<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { AlertCircle } from 'lucide-vue-next'
import Badge from '@/components/ui/Badge.vue'
import Empty from '@/components/ui/Empty.vue'
import Skeleton from '@/components/ui/Skeleton.vue'
import { formatBytes, formatRelativeTime } from '@/utils/format'
import type { CompletedTask } from '@/api/stopped'

/**
 * Last N failed downloads (task status === "error"). The AFD stopped bucket
 * carries no completion timestamp, so the first moment we observe a gid is
 * recorded locally and used for the relative label.
 */

interface Props {
  items: CompletedTask[]
  loading?: boolean
  /** failure timestamps by gid, tracked client-side (backend has none) */
  failedAt?: Record<string, number>
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  failedAt: () => ({}),
})

const emit = defineEmits<{ (e: 'select', task: CompletedTask): void }>()

const { t, locale } = useI18n()

const rows = computed(() =>
  props.items.map((task) => {
    const ts = props.failedAt[task.gid]
    return {
      task,
      size: formatBytes(task.totalLength || task.completedLength),
      when: ts ? formatRelativeTime(ts, locale.value) : t('dashboard.justTracked'),
    }
  }),
)
</script>

<template>
  <div>
    <div v-if="loading" class="space-y-3">
      <div v-for="i in 3" :key="i" class="flex items-center gap-3">
        <Skeleton width="1rem" height="1rem" rounded="full" />
        <Skeleton width="40%" height="0.875rem" />
        <Skeleton width="4rem" height="0.875rem" class="ml-auto" />
      </div>
    </div>

    <Empty
      v-else-if="rows.length === 0"
      :title="t('dashboard.noErrorsCard')"
      :description="t('dashboard.noErrorsCardHint')"
    />

    <ul v-else class="divide-y divide-border">
      <li v-for="row in rows" :key="row.task.gid">
        <button
          type="button"
          class="flex w-full items-center gap-3 rounded-md px-1 py-2.5 text-left transition-colors duration-fast hover:bg-bg-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          @click="emit('select', row.task)"
        >
          <AlertCircle :size="16" class="shrink-0 text-danger" />

          <span class="min-w-0 flex-1">
            <span class="block truncate text-sm text-fg" :title="row.task.path || row.task.name">
              {{ row.task.name }}
            </span>
            <span v-if="row.task.errorMessage" class="block truncate text-xs text-danger">
              {{ row.task.errorMessage }}
            </span>
          </span>

          <Badge variant="danger" size="sm">{{ t('dashboard.statusError') }}</Badge>

          <span class="shrink-0 tabular-nums text-xs text-fg-muted">{{ row.size }}</span>
          <span class="hidden shrink-0 text-xs text-fg-subtle sm:inline">{{ row.when }}</span>
        </button>
      </li>
    </ul>
  </div>
</template>
